import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';

// ==================== Sub-components ====================

const CollapsibleSection = ({ title, defaultOpen = true, badge, borderColor, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '20px', overflow: 'hidden',
      borderLeft: borderColor ? `4px solid ${borderColor}` : undefined,
    }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', cursor: 'pointer', userSelect: 'none',
          backgroundColor: open ? '#fff' : '#fafafa',
          borderBottom: open ? '1px solid #f0f0f0' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#333', margin: 0 }}>{title}</h3>
          {badge && <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '10px', backgroundColor: badge.bg || '#e3f2fd', color: badge.color || '#1976d2', fontWeight: '500' }}>{badge.text}</span>}
        </div>
        <span style={{ fontSize: '16px', color: '#999', transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', lineHeight: 1 }}>&#9660;</span>
      </div>
      {open && <div style={{ padding: '20px' }}>{children}</div>}
    </div>
  );
};

const FlagBadge = ({ value }) => {
  const v = (value || '').trim().toUpperCase();
  if (v === 'T' || v === 'SIM') return <span style={{ ...styles.badge, backgroundColor: '#dcfce7', color: '#166534' }}>T</span>;
  if (v === 'F' || v === 'NAO' || v === 'NÃO') return <span style={{ ...styles.badge, backgroundColor: '#fee2e2', color: '#991b1b' }}>F</span>;
  return <span style={{ ...styles.badge, backgroundColor: '#f3f4f6', color: '#6b7280' }}>NULL</span>;
};

const ValueBadge = ({ value, label }) => {
  const v = (value || '').trim();
  if (!v) return <span style={{ ...styles.badge, backgroundColor: '#f3f4f6', color: '#6b7280' }}>{label || '\u2014'}</span>;
  return <span style={{ ...styles.badge, backgroundColor: '#e0e7ff', color: '#3730a3' }}>{label || v}</span>;
};

const PercentBar = ({ value, total, label }) => {
  const pct = total > 0 ? ((value / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '180px' }}>
      <div style={{ flex: 1, height: '8px', backgroundColor: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', backgroundColor: pct > 50 ? '#22c55e' : pct > 10 ? '#f59e0b' : '#ef4444', borderRadius: '4px', transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: '12px', color: '#666', fontWeight: 500, minWidth: '50px', textAlign: 'right' }}>{label || `${pct.toFixed(1)}%`}</span>
    </div>
  );
};

// ==================== Main Component ====================

const WhatsAppDiagnostico = () => {
  const [activeTab, setActiveTab] = useState('operacional');

  // Operacional state
  const [opCards, setOpCards] = useState(null);
  const [opSelecionado, setOpSelecionado] = useState(null); // qual card está aberto
  const [opDetalhe, setOpDetalhe] = useState([]);
  const [opLoading, setOpLoading] = useState(false);

  const carregarOpResumo = useCallback(async () => {
    try {
      setOpLoading(true);
      const res = await axios.get(`${API_URL}/whatsapp/diagnostico/resumo-cards`);
      setOpCards(res.data);
    } catch (err) { console.error(err); }
    finally { setOpLoading(false); }
  }, []);

  // Auto-load do resumo operacional só quando a aba ficar ativa e ainda
  // não tiver dados — em useEffect (não no body do render, que causava
  // re-renders infinitos: setState dentro de render → React error #301).
  useEffect(() => {
    if (activeTab === 'operacional' && !opCards) {
      carregarOpResumo();
    }
  }, [activeTab, opCards, carregarOpResumo]);

  const abrirCard = useCallback(async (cardId) => {
    setOpSelecionado(cardId);
    setOpDetalhe([]);
    try {
      setOpLoading(true);
      const endpoints = {
        'retry-stuck': '/whatsapp/diagnostico/retry-stuck',
        'reenvios-pendentes': '/whatsapp/diagnostico/reenvios-pendentes',
        'loops': '/whatsapp/diagnostico/loops',
        'n8-n9': '/whatsapp/diagnostico/n8-n9',
        'sem-consentimento': '/whatsapp/diagnostico/sem-consentimento',
        'pagamento-antigo': '/whatsapp/diagnostico/pagamento-antigo',
      };
      const res = await axios.get(`${API_URL}${endpoints[cardId]}`);
      setOpDetalhe(res.data);
    } catch (err) { console.error(err); }
    finally { setOpLoading(false); }
  }, []);

  const resetarRetry = async (solId) => {
    if (!window.confirm(`Resetar tentativas da solicitação ${solId}?`)) return;
    try {
      await axios.post(`${API_URL}/whatsapp/diagnostico/resetar-retry/${solId}`);
      await abrirCard('retry-stuck');
      await carregarOpResumo();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };

  const reenviarSol = async (solId, telefone, pacienteNome, pacienteCodigo) => {
    if (!window.confirm(`Forçar reenvio da solicitação ${solId}?`)) return;
    try {
      await axios.post(`${API_URL}/whatsapp/enviar`, {
        solicitacao_id: solId, telefone, paciente_nome: pacienteNome, paciente_codigo: pacienteCodigo, forcar: true,
      });
      alert('Enviado com sucesso!');
      await abrirCard(opSelecionado);
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Visão Geral state
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(() => new Date().toISOString().split('T')[0]);
  const [stats, setStats] = useState(null);

  // Consulta por Atendimento state
  const [atendimentoId, setAtendimentoId] = useState('');
  const [atendimento, setAtendimento] = useState(null);

  // ==================== Handlers ====================

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/diagnostico/stats`, {
        params: { dataInicio, dataFim }
      });
      setStats(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim]);

  const fetchAtendimento = useCallback(async () => {
    if (!atendimentoId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/diagnostico/atendimento/${atendimentoId}`);
      setAtendimento(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setAtendimento(null);
    } finally {
      setLoading(false);
    }
  }, [atendimentoId]);

  // ==================== Render Helpers ====================

  const renderFlagRow = (label, tVal, fVal, nullVal, total) => (
    <tr key={label}>
      <td style={styles.tdLabel}>{label}</td>
      <td style={styles.tdNum}>{(tVal || 0).toLocaleString('pt-BR')}</td>
      <td style={styles.tdNum}>{(fVal || 0).toLocaleString('pt-BR')}</td>
      <td style={styles.tdNum}>{(nullVal || 0).toLocaleString('pt-BR')}</td>
      <td style={{ ...styles.tdNum, minWidth: '180px' }}>
        <PercentBar value={tVal || 0} total={total} />
      </td>
    </tr>
  );

  const renderContactRow = (label, filledVal, emptyVal, total) => (
    <tr key={label}>
      <td style={styles.tdLabel}>{label}</td>
      <td style={styles.tdNum}>{(filledVal || 0).toLocaleString('pt-BR')}</td>
      <td style={styles.tdNum}>{(emptyVal || 0).toLocaleString('pt-BR')}</td>
      <td style={{ ...styles.tdNum, minWidth: '180px' }}>
        <PercentBar value={filledVal || 0} total={total} />
      </td>
    </tr>
  );

  // ==================== Tab: Visão Geral ====================

  const renderVisaoGeral = () => (
    <>
      {/* Filtros */}
      <div style={styles.filtersCard}>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Data Início</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={styles.filterInput} />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Data Fim</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={styles.filterInput} />
          </div>
          <button onClick={fetchStats} disabled={loading} style={styles.filterBtn}>
            {loading ? 'Carregando...' : 'Pesquisar'}
          </button>
        </div>
      </div>

      {error && <div style={styles.errorMsg}>{error}</div>}

      {stats && (
        <>
          {/* Cards Resumo */}
          <div style={styles.cardsRow}>
            <div style={styles.card}>
              <div style={styles.cardValue}>{stats.solicitacao.total.toLocaleString('pt-BR')}</div>
              <div style={styles.cardLabel}>Solicitacoes</div>
            </div>
            <div style={styles.card}>
              <div style={{ ...styles.cardValue, color: '#166534' }}>{stats.paciente.total > 0 ? ((stats.paciente.whatsapp_t / stats.paciente.total) * 100).toFixed(1) : 0}%</div>
              <div style={styles.cardLabel}>Consentimento (PACIENTE)</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardValue}>{stats.paciente.total > 0 ? ((stats.paciente.celular_preenchido / stats.paciente.total) * 100).toFixed(1) : 0}%</div>
              <div style={styles.cardLabel}>Com Telefone</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardValue}>{stats.solicitacao.total > 0 ? ((stats.solicitacao.resultado_lib_t / stats.solicitacao.total) * 100).toFixed(1) : 0}%</div>
              <div style={styles.cardLabel}>PDF Liberado</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardValue}>{stats.funil ? `${stats.solicitacao.total > 0 ? ((stats.funil.pago_ou_convenio / stats.solicitacao.total) * 100).toFixed(1) : 0}%` : '-'}</div>
              <div style={styles.cardLabel}>Pago/Convenio</div>
            </div>
            <div style={styles.card}>
              <div style={{ ...styles.cardValue, color: '#1d4ed8' }}>{(stats.funil?.elegiveis || 0).toLocaleString('pt-BR')}</div>
              <div style={styles.cardLabel}>Elegiveis p/ Envio</div>
            </div>
          </div>

          {/* FUNIL DE ENVIO */}
          {stats.funil && (
            <CollapsibleSection title="Funil de Envio WhatsApp" badge={{ text: 'pipeline do worker', bg: '#dbeafe', color: '#1d4ed8' }} borderColor="#1d4ed8">
              <div style={{ marginBottom: '16px', fontSize: '12px', color: '#6b7280', lineHeight: '1.6', backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '6px', borderLeft: '3px solid #3b82f6' }}>
                O worker usa <code style={{ backgroundColor: '#e0e7ff', padding: '1px 4px', borderRadius: '3px' }}>PACIENTE.ENVIA_WHATSAPP = 'T'</code> como campo de consentimento.
                Os campos <code style={{ backgroundColor: '#f3f4f6', padding: '1px 4px', borderRadius: '3px' }}>SOLICITACAO.ENVIAR_NOTIFICACAO_EMAIL</code> e <code style={{ backgroundColor: '#f3f4f6', padding: '1px 4px', borderRadius: '3px' }}>SOLICITACAO.ENVIAR_SMS</code> nao sao utilizados pelo envio automatico.
              </div>
              {(() => {
                const f = stats.funil;
                const total = f.total || 1;
                const steps = [
                  { label: 'Total de Solicitacoes', value: f.total, color: '#6b7280' },
                  { label: 'Com Consentimento (ENVIA_WHATSAPP=T)', value: f.com_consentimento, color: '#8b5cf6' },
                  { label: 'Com Telefone (WhatsApp ou Celular)', value: f.com_telefone, color: '#3b82f6' },
                  { label: 'Consentimento + Telefone', value: f.consentimento_e_telefone, color: '#0ea5e9' },
                  { label: 'Com PDF (Resultado Liberado + Senha)', value: f.com_pdf, color: '#f59e0b' },
                  { label: 'Pago ou Convenio (sem valor cobrado)', value: f.pago_ou_convenio, color: '#06b6d4' },
                  { label: 'Nao Pago (particular pendente)', value: f.nao_pago, color: '#ef4444' },
                  { label: 'Elegiveis (Consent + Tel + PDF)', value: f.elegiveis, color: '#22c55e' },
                  ...(stats.workerConfig?.exigir_pagamento ? [
                    { label: 'Elegiveis c/ Pagamento', value: f.elegiveis_com_pagamento, color: '#16a34a' },
                  ] : []),
                ];
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {steps.map((step, i) => {
                      const pct = total > 0 ? (step.value / total * 100) : 0;
                      const barWidth = Math.max(pct, 2);
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ minWidth: '300px', fontSize: '13px', color: '#374151', fontWeight: i === steps.length - 1 ? 600 : 400 }}>{step.label}</div>
                          <div style={{ flex: 1, height: '24px', backgroundColor: '#f3f4f6', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                            <div style={{ width: `${barWidth}%`, height: '100%', backgroundColor: step.color, borderRadius: '4px', transition: 'width 0.5s', opacity: 0.85 }} />
                            <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                              {step.value.toLocaleString('pt-BR')} ({pct.toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {stats.workerConfig?.apenas_consentimento === false && stats.funil.elegiveis_sem_consentimento > 0 && (
                <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '12px', color: '#92400e', borderLeft: '3px solid #f59e0b' }}>
                  Se o filtro de consentimento estiver desativado: <strong>{stats.funil.elegiveis_sem_consentimento.toLocaleString('pt-BR')}</strong> solicitacoes seriam elegiveis (telefone + PDF, sem exigir consentimento).
                </div>
              )}
              {stats.workerConfig?.apenas_consentimento === true && stats.funil.elegiveis_sem_consentimento > stats.funil.elegiveis && (
                <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '12px', color: '#92400e', borderLeft: '3px solid #f59e0b' }}>
                  O filtro de consentimento esta ativo. Sem ele, <strong>{stats.funil.elegiveis_sem_consentimento.toLocaleString('pt-BR')}</strong> solicitacoes seriam elegiveis (vs {stats.funil.elegiveis.toLocaleString('pt-BR')} com filtro) — diferenca de {(stats.funil.elegiveis_sem_consentimento - stats.funil.elegiveis).toLocaleString('pt-BR')}.
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* CONFIG DO WORKER */}
          {stats.workerConfig && (
            <CollapsibleSection title="Configuracao Atual do Worker" badge={{ text: stats.workerConfig.ativo ? 'ativo' : 'inativo', bg: stats.workerConfig.ativo ? '#dcfce7' : '#fee2e2', color: stats.workerConfig.ativo ? '#166534' : '#991b1b' }} borderColor="#0ea5e9" defaultOpen={false}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                {[
                  ['Apenas c/ Consentimento', stats.workerConfig.apenas_consentimento ? 'Sim' : 'Nao', stats.workerConfig.apenas_consentimento ? '#dcfce7' : '#fef3c7', stats.workerConfig.apenas_consentimento ? '#166534' : '#92400e'],
                  ['Exigir Pagamento', stats.workerConfig.exigir_pagamento ? 'Sim' : 'Nao', stats.workerConfig.exigir_pagamento ? '#dcfce7' : '#fef3c7', stats.workerConfig.exigir_pagamento ? '#166534' : '#92400e'],
                  ...(stats.workerConfig.exigir_pagamento ? [['Dias Retro. Pagamento', `${stats.workerConfig.pagamento_dias_retroativos} dias`, '#e0e7ff', '#3730a3']] : []),
                  ['Exigir PDF', stats.workerConfig.exigir_pdf ? 'Sim' : 'Nao', stats.workerConfig.exigir_pdf ? '#dcfce7' : '#fef3c7', stats.workerConfig.exigir_pdf ? '#166534' : '#92400e'],
                  ['Status Minimo', stats.workerConfig.status_minimo, '#e0e7ff', '#3730a3'],
                  ['% Minimo Exames', `${stats.workerConfig.percentual_minimo}%`, '#e0e7ff', '#3730a3'],
                  ['Delay apos Digitacao', `${stats.workerConfig.delay_minutos} min`, '#e0e7ff', '#3730a3'],
                  ['Dias Retroativos', `${stats.workerConfig.dias_retroativos} dias`, '#e0e7ff', '#3730a3'],
                  ['Intervalo Ciclo', `${stats.workerConfig.intervalo_minutos} min`, '#e0e7ff', '#3730a3'],
                  ['Horario', `${stats.workerConfig.horario_inicio} - ${stats.workerConfig.horario_fim}`, '#e0e7ff', '#3730a3'],
                  ['TAT Inteligente', stats.workerConfig.tat_envio_inteligente ? 'Sim' : 'Nao', stats.workerConfig.tat_envio_inteligente ? '#dcfce7' : '#f3f4f6', stats.workerConfig.tat_envio_inteligente ? '#166534' : '#6b7280'],
                  ['PDF Interno', stats.workerConfig.usar_pdf_interno ? 'Sim' : 'Nao', stats.workerConfig.usar_pdf_interno ? '#dcfce7' : '#f3f4f6', stats.workerConfig.usar_pdf_interno ? '#166534' : '#6b7280'],
                ].map(([label, value, bg, color]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
                    <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>{label}</span>
                    <span style={{ ...styles.badge, backgroundColor: bg, color }}>{value}</span>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* SOLICITACAO */}
          <CollapsibleSection title="SOLICITACAO — Flags de Notificação" badge={{ text: `${stats.solicitacao.total.toLocaleString('pt-BR')} solicitações` }} borderColor="#3b82f6">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Campo</th>
                  <th style={styles.thNum}>Sim (T)</th>
                  <th style={styles.thNum}>Não (F)</th>
                  <th style={styles.thNum}>NULL</th>
                  <th style={styles.thNum}>% Sim</th>
                </tr>
              </thead>
              <tbody>
                {renderFlagRow('ENVIAR_NOTIFICACAO_EMAIL', stats.solicitacao.notif_email_t, stats.solicitacao.notif_email_f, stats.solicitacao.notif_email_null, stats.solicitacao.total)}
                {renderFlagRow('ENVIAR_SMS', stats.solicitacao.sms_t, stats.solicitacao.sms_f, stats.solicitacao.sms_null, stats.solicitacao.total)}
                {renderFlagRow('ENVIA_SLINE_PAC', stats.solicitacao.sline_pac_t, stats.solicitacao.sline_pac_f, stats.solicitacao.sline_pac_null, stats.solicitacao.total)}
                {renderFlagRow('SMS_COMPLETO_ENVIADO', stats.solicitacao.sms_compl_t, stats.solicitacao.sms_compl_f, stats.solicitacao.sms_compl_null, stats.solicitacao.total)}
                {renderFlagRow('SMS_INTERM_ENVIADO', stats.solicitacao.sms_interm_t, stats.solicitacao.sms_interm_f, stats.solicitacao.sms_interm_null, stats.solicitacao.total)}
                {renderFlagRow('RESULTADO_LIBERADO', stats.solicitacao.resultado_lib_t, stats.solicitacao.resultado_lib_f, stats.solicitacao.resultado_lib_null, stats.solicitacao.total)}
              </tbody>
            </table>
          </CollapsibleSection>

          {/* PACIENTE */}
          <CollapsibleSection title="PACIENTE — Dados de Contato" badge={{ text: `${stats.paciente.total.toLocaleString('pt-BR')} pacientes` }} borderColor="#8b5cf6">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Campo</th>
                  <th style={styles.thNum}>Sim (T)</th>
                  <th style={styles.thNum}>Não (F)</th>
                  <th style={styles.thNum}>NULL</th>
                  <th style={styles.thNum}>% Sim</th>
                </tr>
              </thead>
              <tbody>
                {renderFlagRow('ENVIA_WHATSAPP', stats.paciente.whatsapp_t, stats.paciente.whatsapp_f, stats.paciente.whatsapp_null, stats.paciente.total)}
              </tbody>
            </table>
            <div style={{ marginTop: '16px' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Campo</th>
                    <th style={styles.thNum}>Preenchido</th>
                    <th style={styles.thNum}>Vazio</th>
                    <th style={styles.thNum}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {renderContactRow('WHATSAPP', stats.paciente.whatsapp_preenchido, stats.paciente.whatsapp_vazio, stats.paciente.total)}
                  {renderContactRow('CELULAR', stats.paciente.celular_preenchido, stats.paciente.celular_vazio, stats.paciente.total)}
                  {renderContactRow('EMAIL', stats.paciente.email_preenchido, stats.paciente.email_vazio, stats.paciente.total)}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>

          {/* SOLICITACAO_EXAMES */}
          <CollapsibleSection title="SOLICITACAO_EXAMES — Flags de Envio" badge={{ text: `${stats.exames.total.toLocaleString('pt-BR')} exames` }} borderColor="#f59e0b">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Campo</th>
                  <th style={styles.thNum}>Sim (T)</th>
                  <th style={styles.thNum}>Não (F)</th>
                  <th style={styles.thNum}>NULL</th>
                  <th style={styles.thNum}>% Sim</th>
                </tr>
              </thead>
              <tbody>
                {renderFlagRow('ENVIADO_EMAIL', stats.exames.email_t, stats.exames.email_f, stats.exames.email_null, stats.exames.total)}
                {renderFlagRow('ENVIADO_SLINE', stats.exames.sline_t, stats.exames.sline_f, stats.exames.sline_null, stats.exames.total)}
                {renderFlagRow('ENVIADO_FTP', stats.exames.ftp_t, stats.exames.ftp_f, stats.exames.ftp_null, stats.exames.total)}
              </tbody>
            </table>
          </CollapsibleSection>

          {/* CONVENIO */}
          <CollapsibleSection title="CONVENIO — Configuração" badge={{ text: `${stats.convenio.total.toLocaleString('pt-BR')} convênios` }} borderColor="#10b981">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Campo</th>
                  <th style={styles.thNum}>Sim (T)</th>
                  <th style={styles.thNum}>Não (F)</th>
                  <th style={styles.thNum}>NULL</th>
                  <th style={styles.thNum}>% Sim</th>
                </tr>
              </thead>
              <tbody>
                {renderFlagRow('ENVIA_SMS', stats.convenio.sms_t, stats.convenio.sms_f, stats.convenio.sms_null, stats.convenio.total)}
                {renderFlagRow('ENVIA_INTERNET', stats.convenio.internet_t, stats.convenio.internet_f, stats.convenio.internet_null, stats.convenio.total)}
              </tbody>
            </table>
            <div style={{ marginTop: '16px' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Campo</th>
                    <th style={styles.thNum}>Preenchido</th>
                    <th style={styles.thNum}>Vazio</th>
                    <th style={styles.thNum}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {renderContactRow('EMAIL_LAUDOS', stats.convenio.email_laudos_preenchido, stats.convenio.email_laudos_vazio, stats.convenio.total)}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>

          {/* PREFERENCIAS */}
          <CollapsibleSection title="PREFERENCIAS — Configuração Global" borderColor="#6366f1">
            <div style={styles.flagGrid}>
              {[
                ['ENVIAR_SMS_INTERNET', stats.preferencias.enviar_sms_internet],
                ['ALERTA_EMAIL_PAC', stats.preferencias.alerta_email_pac],
                ['ALERTA_EMAIL_MED', stats.preferencias.alerta_email_med],
                ['ENVIA_SLINE', stats.preferencias.envia_sline],
              ].map(([label, val]) => (
                <div key={label} style={styles.flagItem}>
                  <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>{label}:</span>
                  <FlagBadge value={val} />
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* FORMA_ENTREGA */}
          <CollapsibleSection title="FORMA_ENTREGA — Canais" badge={{ text: `${stats.formaEntrega.length} registros` }} borderColor="#ec4899">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Descrição</th>
                  <th style={styles.thCenter}>Email Pac</th>
                  <th style={styles.thCenter}>Email Med</th>
                  <th style={styles.thCenter}>Email Conv</th>
                </tr>
              </thead>
              <tbody>
                {stats.formaEntrega.map(fe => (
                  <tr key={fe.id}>
                    <td style={styles.td}>{fe.id}</td>
                    <td style={styles.td}>{fe.descricao}</td>
                    <td style={styles.tdCenter}><FlagBadge value={fe.email_pac} /></td>
                    <td style={styles.tdCenter}><FlagBadge value={fe.email_med} /></td>
                    <td style={styles.tdCenter}><FlagBadge value={fe.email_conv} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollapsibleSection>
        </>
      )}

      {!stats && !loading && !error && (
        <div style={styles.emptyState}>Selecione um período e clique em Pesquisar</div>
      )}
    </>
  );

  // ==================== Tab: Consulta por Atendimento ====================

  const situacaoMap = { P: 'Pendente', M: 'Parcial', D: 'Digitado', F: 'Falta conferir', I: 'Impresso', E: 'Entregue' };

  const renderAtendimento = () => (
    <>
      {/* Busca */}
      <div style={styles.filtersCard}>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>ID Solicitação</label>
            <input
              type="number"
              value={atendimentoId}
              onChange={e => setAtendimentoId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchAtendimento()}
              placeholder="Ex: 321954"
              style={{ ...styles.filterInput, minWidth: '160px' }}
            />
          </div>
          <button onClick={fetchAtendimento} disabled={loading || !atendimentoId} style={styles.filterBtn}>
            {loading ? 'Carregando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {error && <div style={styles.errorMsg}>{error}</div>}

      {atendimento && (
        <>
          {/* Solicitação */}
          <CollapsibleSection
            title={`Solicitação #${atendimento.solicitacao.ID} — Protocolo ${atendimento.solicitacao.LOCAL}/${atendimento.solicitacao.PROTOCOLO}`}
            badge={{ text: atendimento.solicitacao.DATA ? new Date(atendimento.solicitacao.DATA).toLocaleDateString('pt-BR') : '' }}
            borderColor="#3b82f6"
          >
            <div style={styles.flagGrid}>
              {[
                ['ENVIAR_NOTIFICACAO_EMAIL', atendimento.solicitacao.ENVIAR_NOTIFICACAO_EMAIL],
                ['ENVIAR_SMS', atendimento.solicitacao.ENVIAR_SMS],
                ['ENVIA_SLINE_PAC', atendimento.solicitacao.ENVIA_SLINE_PAC],
                ['SMS_COMPLETO_ENVIADO', atendimento.solicitacao.SMS_COMPLETO_ENVIADO],
                ['SMS_INTERM_ENVIADO', atendimento.solicitacao.SMS_INTERM_ENVIADO],
                ['RESULTADO_LIBERADO', atendimento.solicitacao.RESULTADO_LIBERADO],
              ].map(([label, val]) => (
                <div key={label} style={styles.flagItem}>
                  <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>{label}:</span>
                  <FlagBadge value={val} />
                </div>
              ))}
              <div style={styles.flagItem}>
                <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>SENHA_SLINE:</span>
                <ValueBadge value={atendimento.solicitacao.SENHA_SLINE} />
              </div>
            </div>
          </CollapsibleSection>

          {/* Paciente */}
          <CollapsibleSection
            title={`Paciente — ${atendimento.solicitacao.PACIENTE_NOME} (ID: ${atendimento.solicitacao.PACIENTE_ID})`}
            borderColor="#8b5cf6"
          >
            <div style={styles.flagGrid}>
              <div style={styles.flagItem}>
                <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>ENVIA_WHATSAPP:</span>
                <FlagBadge value={atendimento.solicitacao.ENVIA_WHATSAPP} />
              </div>
              <div style={styles.flagItem}>
                <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>WHATSAPP:</span>
                <ValueBadge value={atendimento.solicitacao.WHATSAPP} />
              </div>
              <div style={styles.flagItem}>
                <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>CELULAR:</span>
                <ValueBadge value={atendimento.solicitacao.CELULAR} />
              </div>
              <div style={styles.flagItem}>
                <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>EMAIL:</span>
                <ValueBadge value={atendimento.solicitacao.EMAIL} />
              </div>
            </div>
          </CollapsibleSection>

          {/* Exames */}
          <CollapsibleSection
            title="Exames"
            badge={{ text: `${atendimento.exames.length} exames` }}
            borderColor="#f59e0b"
          >
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Exame</th>
                  <th style={styles.th}>Nome</th>
                  <th style={styles.thCenter}>Situação</th>
                  <th style={styles.thCenter}>Email</th>
                  <th style={styles.thCenter}>SLine</th>
                  <th style={styles.thCenter}>FTP</th>
                </tr>
              </thead>
              <tbody>
                {atendimento.exames.map(ex => (
                  <tr key={ex.EXAME}>
                    <td style={styles.td}><code style={{ fontSize: '12px' }}>{ex.EXAME}</code></td>
                    <td style={styles.td}>{ex.EXAME_NOME}</td>
                    <td style={styles.tdCenter}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: ex.SITUACAO === 'I' || ex.SITUACAO === 'E' ? '#dcfce7' : ex.SITUACAO === 'P' ? '#fef9c3' : '#e0e7ff',
                        color: ex.SITUACAO === 'I' || ex.SITUACAO === 'E' ? '#166534' : ex.SITUACAO === 'P' ? '#854d0e' : '#3730a3',
                      }}>
                        {ex.SITUACAO} {situacaoMap[ex.SITUACAO] ? `(${situacaoMap[ex.SITUACAO]})` : ''}
                      </span>
                    </td>
                    <td style={styles.tdCenter}><FlagBadge value={ex.ENVIADO_EMAIL} /></td>
                    <td style={styles.tdCenter}><FlagBadge value={ex.ENVIADO_SLINE} /></td>
                    <td style={styles.tdCenter}><FlagBadge value={ex.ENVIADO_FTP} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollapsibleSection>

          {/* Convênio */}
          {atendimento.convenio.length > 0 && (
            <CollapsibleSection
              title={`Convênio — ${atendimento.convenio.map(c => c.NOME).join(', ')}`}
              borderColor="#10b981"
            >
              {atendimento.convenio.map(c => (
                <div key={c.ID} style={styles.flagGrid}>
                  <div style={styles.flagItem}>
                    <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>ENVIA_SMS:</span>
                    <FlagBadge value={c.ENVIA_SMS} />
                  </div>
                  <div style={styles.flagItem}>
                    <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>ENVIA_INTERNET:</span>
                    <FlagBadge value={c.ENVIA_INTERNET} />
                  </div>
                  <div style={styles.flagItem}>
                    <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>EMAIL_LAUDOS:</span>
                    <ValueBadge value={c.EMAIL_LAUDOS} />
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}

          {/* Forma de Entrega */}
          {atendimento.formaEntrega && (
            <CollapsibleSection
              title={`Forma de Entrega — ${atendimento.formaEntrega.DESCRICAO || atendimento.formaEntrega.ID}`}
              borderColor="#ec4899"
            >
              <div style={styles.flagGrid}>
                <div style={styles.flagItem}>
                  <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>EMAIL_PAC:</span>
                  <FlagBadge value={atendimento.formaEntrega.EMAIL_PAC} />
                </div>
                <div style={styles.flagItem}>
                  <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>EMAIL_MED:</span>
                  <FlagBadge value={atendimento.formaEntrega.EMAIL_MED} />
                </div>
                <div style={styles.flagItem}>
                  <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>EMAIL_CONV:</span>
                  <FlagBadge value={atendimento.formaEntrega.EMAIL_CONV} />
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Histórico WhatsApp */}
          <CollapsibleSection
            title="Histórico de Envios WhatsApp"
            badge={atendimento.historicoWhatsapp.length > 0 ? { text: `${atendimento.historicoWhatsapp.length} envios` } : { text: 'nenhum', bg: '#f3f4f6', color: '#6b7280' }}
            borderColor="#6366f1"
          >
            {atendimento.historicoWhatsapp.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>Nenhum envio registrado para esta solicitação</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Data</th>
                    <th style={styles.thCenter}>Tipo</th>
                    <th style={styles.thCenter}>Status</th>
                    <th style={styles.th}>Telefone</th>
                    <th style={styles.th}>Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {atendimento.historicoWhatsapp.map(h => (
                    <tr key={h.id}>
                      <td style={styles.td}>{h.created_at ? new Date(h.created_at).toLocaleString('pt-BR') : '\u2014'}</td>
                      <td style={styles.tdCenter}>
                        <span style={{
                          ...styles.badge,
                          backgroundColor: h.tipo === 'automatico' ? '#e0e7ff' : '#fef3c7',
                          color: h.tipo === 'automatico' ? '#3730a3' : '#92400e',
                        }}>{h.tipo}</span>
                      </td>
                      <td style={styles.tdCenter}>
                        <span style={{
                          ...styles.badge,
                          backgroundColor: h.status === 'enviado' ? '#dcfce7' : h.status === 'erro' ? '#fee2e2' : '#f3f4f6',
                          color: h.status === 'enviado' ? '#166534' : h.status === 'erro' ? '#991b1b' : '#6b7280',
                        }}>{h.status}</span>
                      </td>
                      <td style={styles.td}>{h.telefone || '\u2014'}</td>
                      <td style={{ ...styles.td, color: '#ef4444', fontSize: '12px' }}>{h.erro || '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CollapsibleSection>
        </>
      )}

      {!atendimento && !loading && !error && (
        <div style={styles.emptyState}>Digite o ID da solicitação e clique em Buscar</div>
      )}
    </>
  );

  // ==================== Main Render ====================

  const tabs = [
    { id: 'operacional', label: '🚨 Operacional' },
    { id: 'visao-geral', label: 'Visão Geral' },
    { id: 'atendimento', label: 'Consulta por Atendimento' },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={styles.pageIcon}>&#128269;</span>
          <div>
            <h1 style={styles.pageTitle}>Diagnóstico de Notificações</h1>
            <p style={styles.pageSubtitle}>Visão centralizada dos campos de notificação (SQL Server + PostgreSQL)</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setError(null); }}
            style={activeTab === tab.id ? { ...styles.tab, ...styles.tabActive } : styles.tab}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'operacional' && renderOperacional()}
      {activeTab === 'visao-geral' && renderVisaoGeral()}
      {activeTab === 'atendimento' && renderAtendimento()}
    </div>
  );

  function renderOperacional() {
    if (!opCards) {
      // Carregamento dispara via useEffect [activeTab, opCards] — aqui só renderiza loading.
      return <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>Carregando...</div>;
    }
    const cards = [
      { id: 'retry-stuck', titulo: '🚨 Travadas (3+ erros)', desc: 'Worker desistiu', count: opCards.retry_stuck, color: '#c62828' },
      { id: 'reenvios-pendentes', titulo: '⏰ Reenvios pendentes', desc: 'Último envio > 24h', count: null, color: '#ed6c02' },
      { id: 'loops', titulo: '🔁 Loops suspeitos', desc: '> 3 envios em 24h', count: opCards.loops_suspeitos, color: '#d32f2f' },
      { id: 'n8-n9', titulo: '👥 N8/N9 duplicados', desc: 'Contatos com 9º dígito', count: null, color: '#6d4c41' },
      { id: 'sem-consentimento', titulo: '📵 Sem consentimento', desc: 'Exames prontos sem ENVIA_WHATSAPP', count: null, color: '#616161' },
      { id: 'pagamento-antigo', titulo: '💸 Pagamento pendente', desc: '> 7 dias em aberto', count: opCards.notif_pagamento_7d, color: '#7b1fa2' },
    ];

    return (
      <div style={{ padding: '16px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 16 }}>
          {cards.map(c => (
            <div key={c.id} onClick={() => abrirCard(c.id)}
              style={{
                background: '#fff', padding: 16, borderRadius: 8, cursor: 'pointer',
                borderLeft: `4px solid ${c.color}`, boxShadow: '0 1px 3px rgba(0,0,0,.08)',
                transition: 'transform .15s', ...(opSelecionado === c.id ? { transform: 'scale(1.01)', boxShadow: '0 3px 10px rgba(0,0,0,.12)' } : {}),
              }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 4 }}>{c.titulo}</div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{c.desc}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>
                {c.count !== null && c.count !== undefined ? c.count : '—'}
              </div>
            </div>
          ))}
        </div>

        {opSelecionado && (
          <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 15 }}>
              Detalhes: {cards.find(c => c.id === opSelecionado)?.titulo}
              <button onClick={() => setOpSelecionado(null)} style={{ float: 'right', fontSize: 12, background: '#fff', border: '1px solid #d1d5db', padding: '4px 10px', borderRadius: 4, cursor: 'pointer' }}>Fechar</button>
            </h3>
            {opLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>Carregando...</div>
            ) : opDetalhe.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#2e7d32' }}>✅ Nenhum item encontrado</div>
            ) : (
              <OpTable cardId={opSelecionado} items={opDetalhe} onResetarRetry={resetarRetry} onReenviar={reenviarSol} />
            )}
          </div>
        )}
      </div>
    );
  }
};

// Tabela genérica por tipo de card
const OpTable = ({ cardId, items, onResetarRetry, onReenviar }) => {
  const fmt = (d) => d ? new Date(d).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '';
  const formatTel = (t) => t ? t.replace(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/, '$1 $2 $3-$4') : '';

  if (cardId === 'retry-stuck') {
    return (
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f5f5f5' }}>
          <th style={thStyleD}>Sol</th><th style={thStyleD}>Paciente</th>
          <th style={thStyleD}>Telefone</th><th style={thStyleD}>Tentativas</th>
          <th style={thStyleD}>Última</th><th style={thStyleD}>Último erro</th>
          <th style={thStyleD}>Ação</th>
        </tr></thead>
        <tbody>
          {items.map(r => (
            <tr key={r.solicitacao_id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={tdStyleD}>{r.solicitacao_id}</td>
              <td style={tdStyleD}>{r.paciente_nome}</td>
              <td style={tdStyleD}>{formatTel(r.telefone)}</td>
              <td style={tdStyleD}><strong style={{ color: '#c62828' }}>{r.tentativas}</strong></td>
              <td style={tdStyleD}><small>{fmt(r.ultima_tentativa)}</small></td>
              <td style={tdStyleD}><small style={{ color: '#c62828' }}>{r.ultimo_erro?.substring(0, 60)}</small></td>
              <td style={tdStyleD}><button onClick={() => onResetarRetry(r.solicitacao_id)} style={btnAcao}>Resetar</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (cardId === 'reenvios-pendentes') {
    return (
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f5f5f5' }}>
          <th style={thStyleD}>Sol</th><th style={thStyleD}>Paciente</th><th style={thStyleD}>Telefone</th>
          <th style={thStyleD}>Último envio</th><th style={thStyleD}>Ação</th>
        </tr></thead>
        <tbody>
          {items.map(r => (
            <tr key={r.solicitacao_id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={tdStyleD}>{r.solicitacao_id}</td>
              <td style={tdStyleD}>{r.paciente_nome}</td>
              <td style={tdStyleD}>{formatTel(r.telefone)}</td>
              <td style={tdStyleD}><small>{fmt(r.enviado_em)}</small></td>
              <td style={tdStyleD}><button onClick={() => onReenviar(r.solicitacao_id, r.telefone, r.paciente_nome)} style={btnAcao}>Reenviar</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (cardId === 'loops') {
    return (
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f5f5f5' }}>
          <th style={thStyleD}>Sol</th><th style={thStyleD}>Paciente</th><th style={thStyleD}>Telefone</th>
          <th style={thStyleD}>Envios</th><th style={thStyleD}>Primeiro</th><th style={thStyleD}>Último</th>
          <th style={thStyleD}>Tipos</th>
        </tr></thead>
        <tbody>
          {items.map(r => (
            <tr key={r.solicitacao_id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={tdStyleD}>{r.solicitacao_id}</td>
              <td style={tdStyleD}>{r.paciente_nome}</td>
              <td style={tdStyleD}>{formatTel(r.telefone)}</td>
              <td style={tdStyleD}><strong style={{ color: '#d32f2f' }}>{r.total_envios}</strong></td>
              <td style={tdStyleD}><small>{fmt(r.primeiro)}</small></td>
              <td style={tdStyleD}><small>{fmt(r.ultimo)}</small></td>
              <td style={tdStyleD}><small>{(r.tipos || []).join(', ')}</small></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (cardId === 'n8-n9') {
    return (
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f5f5f5' }}>
          <th style={thStyleD}>Número base</th><th style={thStyleD}>N8 (sem 9)</th><th style={thStyleD}>N9 (com 9)</th>
        </tr></thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
              <td style={tdStyleD}>{r.parte_unica}</td>
              <td style={tdStyleD}><code style={{ color: '#c62828' }}>{r.n8}</code></td>
              <td style={tdStyleD}><code style={{ color: '#2e7d32' }}>{r.n9}</code></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (cardId === 'sem-consentimento') {
    return (
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f5f5f5' }}>
          <th style={thStyleD}>Sol</th><th style={thStyleD}>Protocolo</th><th style={thStyleD}>Paciente</th>
          <th style={thStyleD}>Telefone</th><th style={thStyleD}>ENVIA</th><th style={thStyleD}>Exames prontos</th>
        </tr></thead>
        <tbody>
          {items.map(r => (
            <tr key={r.SOLICITACAO_ID} style={{ borderBottom: '1px solid #eee' }}>
              <td style={tdStyleD}>{r.SOLICITACAO_ID}</td>
              <td style={tdStyleD}>{r.PROTOCOLO}</td>
              <td style={tdStyleD}>{r.PACIENTE_NOME}</td>
              <td style={tdStyleD}><small>{r.WHATSAPP || r.CELULAR}</small></td>
              <td style={tdStyleD}><code style={{ color: '#c62828' }}>{r.ENVIA_WHATSAPP || 'NULL'}</code></td>
              <td style={tdStyleD}>{r.EXAMES_PRONTOS}/{r.TOTAL_EXAMES}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (cardId === 'pagamento-antigo') {
    return (
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f5f5f5' }}>
          <th style={thStyleD}>Sol</th><th style={thStyleD}>Protocolo</th><th style={thStyleD}>Paciente</th>
          <th style={thStyleD}>Cobrado</th><th style={thStyleD}>Pago</th><th style={thStyleD}>Dias</th>
          <th style={thStyleD}>Notificado?</th>
        </tr></thead>
        <tbody>
          {items.map(r => (
            <tr key={r.SOLICITACAO_ID} style={{ borderBottom: '1px solid #eee' }}>
              <td style={tdStyleD}>{r.SOLICITACAO_ID}</td>
              <td style={tdStyleD}>{r.PROTOCOLO}</td>
              <td style={tdStyleD}>{r.PACIENTE_NOME}</td>
              <td style={tdStyleD}>R$ {Number(r.VALOR_COBRADO).toFixed(2)}</td>
              <td style={tdStyleD}>R$ {Number(r.VALOR_PAGO).toFixed(2)}</td>
              <td style={tdStyleD}>{r.DIAS_ATRAS}</td>
              <td style={tdStyleD}>{r.JA_NOTIFICADA ? '✅' : '❌'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return null;
};

const thStyleD = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', fontSize: 12 };
const tdStyleD = { padding: '8px 10px', verticalAlign: 'middle' };
const btnAcao = { background: '#1976d2', color: '#fff', border: 'none', padding: '5px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12 };

// ==================== Styles ====================

const styles = {
  container: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    fontFamily: 'Inter, Roboto, "Open Sans", sans-serif',
    padding: '0',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: { fontSize: '1.5rem', lineHeight: 1 },
  pageTitle: { fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', margin: '0 0 2px 0' },
  pageSubtitle: { fontSize: '0.85rem', color: '#666', margin: 0 },
  tabBar: {
    display: 'flex',
    gap: '0',
    marginBottom: '20px',
    borderBottom: '2px solid #e5e7eb',
  },
  tab: {
    padding: '10px 20px',
    border: 'none',
    background: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'color 0.15s, border-color 0.15s',
  },
  tabActive: {
    color: '#1d4ed8',
    borderBottomColor: '#1d4ed8',
    fontWeight: 600,
  },
  filtersCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  filtersRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filterInput: {
    padding: '7px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
    outline: 'none',
    minWidth: '140px',
  },
  filterBtn: {
    padding: '8px 18px',
    borderRadius: '6px',
    border: 'none',
    background: '#1d4ed8',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  errorMsg: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '10px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
    fontSize: '13px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '12px',
    marginBottom: '20px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  cardValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1a1a2e',
  },
  cardLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px',
    fontWeight: 500,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: '2px solid #e5e7eb',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  thNum: {
    textAlign: 'right',
    padding: '8px 12px',
    borderBottom: '2px solid #e5e7eb',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  thCenter: {
    textAlign: 'center',
    padding: '8px 12px',
    borderBottom: '2px solid #e5e7eb',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  td: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
  },
  tdLabel: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    fontFamily: 'monospace',
    fontSize: '12px',
    fontWeight: 500,
  },
  tdNum: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    textAlign: 'right',
    color: '#374151',
  },
  tdCenter: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    textAlign: 'center',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.3px',
  },
  flagGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px 32px',
  },
  flagItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};

export default WhatsAppDiagnostico;
